// =============================================================================
// MeuDriver — useStudentAddresses
// -----------------------------------------------------------------------------
// CRUD do aluno sobre `student_addresses` + helpers (definir default).
// =============================================================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/hooks/useAuth'

export interface StudentAddress {
  id: string
  student_id: string
  label: string
  address: string
  lat: number | null
  lng: number | null
  is_default: boolean
  created_at: string
}

export interface NewStudentAddress {
  label: string
  address: string
  lat?: number | null
  lng?: number | null
  is_default?: boolean
}

export function useStudentAddresses() {
  const { user } = useAuth()
  const qc = useQueryClient()
  const userId = user?.id

  const query = useQuery({
    queryKey: ['student-addresses', userId],
    enabled: !!userId,
    queryFn: async (): Promise<StudentAddress[]> => {
      const { data, error } = await supabase
        .from('student_addresses')
        .select('id, student_id, label, address, lat, lng, is_default, created_at')
        .order('is_default', { ascending: false })
        .order('created_at', { ascending: false })
      if (error) throw error
      return (data ?? []) as StudentAddress[]
    },
  })

  const create = useMutation({
    mutationFn: async (input: NewStudentAddress) => {
      if (!userId) throw new Error('Você precisa estar logado.')
      const { data, error } = await supabase
        .from('student_addresses')
        .insert({
          student_id: userId,
          label: input.label,
          address: input.address,
          lat: input.lat ?? null,
          lng: input.lng ?? null,
          is_default: input.is_default ?? false,
        })
        .select()
        .single()
      if (error) throw error
      return data as StudentAddress
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['student-addresses', userId] }),
  })

  const update = useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: Partial<NewStudentAddress> }) => {
      const { data, error } = await supabase
        .from('student_addresses')
        .update(patch)
        .eq('id', id)
        .select()
        .single()
      if (error) throw error
      return data as StudentAddress
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['student-addresses', userId] }),
  })

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('student_addresses').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['student-addresses', userId] }),
  })

  const setDefault = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.rpc('set_default_student_address', { p_address_id: id })
      if (error) throw error
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['student-addresses', userId] }),
  })

  return {
    addresses: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error,
    create,
    update,
    remove,
    setDefault,
  }
}
